import React, { useState, useMemo, useRef } from 'react';
import { Building2, LogIn, LogOut, Check, Users, KeyRound, ArrowLeft, Printer, Camera } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const VISITOR_TYPES = [
  { value: 'staff',    label: 'Staff'            },
  { value: 'client',   label: 'Client'           },
  { value: 'parent',   label: 'Parent/Guardian'  },
  { value: 'provider', label: 'Therapy Provider' },
  { value: 'visitor',  label: 'Visitor'          },
  { value: 'other',    label: 'Other'            },
];

const PINS_KEY = 'evoke-visitor-pins';

// ─── PIN helpers ──────────────────────────────────────────────────────────────

const getPins = () => {
  try { return JSON.parse(localStorage.getItem(PINS_KEY) || '{}'); }
  catch { return {}; }
};

const savePins = (pins) => {
  localStorage.setItem(PINS_KEY, JSON.stringify(pins));
};

const isValidPin = (pin) => /^\d{4}$/.test(pin);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function VisitorKiosk({
  attendanceRecords = [],
  today,
  onCheckIn,
  onCheckOut,
  onSwitchToBuilding,
}) {
  // step: 'home' | 'checkin' | 'checkout' | 'pin-entry' | 'forgot-pin' | 'returning' | 'pin-success' | 'return-success' | 'success'
  const [step, setStep] = useState('home');
  const [form, setForm] = useState({
    name: '', type: 'visitor', purpose: '', host: '', organization: '', pin: '', photo: '',
  });
  const [pinInput, setPinInput] = useState('');
  const [returnPhoto, setReturnPhoto] = useState('');
  const photoInputRef = useRef(null);
  const returnPhotoInputRef = useRef(null);
  const [pinError, setPinError] = useState('');
  const [checkInPinError, setCheckInPinError] = useState('');
  const [returnVisitor, setReturnVisitor] = useState(null); // { name, type, organization, pin }
  const [returnHost, setReturnHost] = useState('');
  const [newPin, setNewPin] = useState('');       // shown after first check-in
  const [successMsg, setSuccessMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [forgotName, setForgotName] = useState('');
  const [foundPin, setFoundPin] = useState(null); // { pin, name } or null
  const [badgeData, setBadgeData] = useState(null); // { name, type, host, date }

  const currentlyIn = useMemo(
    () => attendanceRecords.filter(r => r.isCheckedIn),
    [attendanceRecords]
  );

  const resetAll = () => {
    setForm({ name: '', type: 'visitor', purpose: '', host: '', organization: '', pin: '', photo: '' });
    setPinInput('');
    setPinError('');
    setCheckInPinError('');
    setReturnVisitor(null);
    setReturnPhoto('');
    setReturnHost('');
    setNewPin('');
    setSuccessMsg('');
    setSubmitting(false);
    setForgotName('');
    setFoundPin(null);
    setBadgeData(null);
  };

  const handlePrintBadge = () => {
    window.print();
  };

  const goHome = () => { resetAll(); setStep('home'); };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setStep('success');
    setTimeout(() => { goHome(); }, 4000);
  };

  // ── New visitor check-in ────────────────────────────────────────────────────

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, photo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleReturnPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setReturnPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleNewCheckIn = async () => {
    if (!form.name.trim() || submitting) return;

    const selectedPin = form.pin.trim();
    if (!isValidPin(selectedPin)) {
      setCheckInPinError('Please choose a 4-digit PIN.');
      return;
    }

    const pins = getPins();
    const existingProfile = pins[selectedPin];
    const samePerson = existingProfile
      ? existingProfile.name.toLowerCase() === form.name.trim().toLowerCase()
      : false;
    if (existingProfile && !samePerson) {
      setCheckInPinError('That PIN is already in use. Please choose a different 4-digit PIN.');
      return;
    }

    setCheckInPinError('');
    setSubmitting(true);

    const pin = selectedPin;
    const visitorId = `visitor-${Date.now()}`;

    // Save visitor profile to localStorage for future PIN logins
    pins[pin] = {
      name: form.name.trim(),
      type: form.type,
      organization: form.organization,
      createdAt: new Date().toISOString(),
    };
    savePins(pins);

    try {
      await onCheckIn({
        personId:     visitorId,
        personId_sp:  0,
        personName:   form.name.trim(),
        personType:   form.type,
        purpose:      form.purpose,
        host:         form.host,
        organization: form.organization,
      });
    } catch { /* optimistic — UI still updates */ }

    setNewPin(pin);
    setSuccessMsg(`Welcome, ${form.name.trim()}! You are checked in.`);
    const badge = {
      name: form.name.trim(),
      type: form.type,
      host: form.host,
      organization: form.organization,
      photo: form.photo || '',
      date: new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
    resetAll();
    setNewPin(pin); // resetAll clears it, set again
    setBadgeData(badge);
    setStep('pin-success');
    setSubmitting(false);
  };

  // ── Returning visitor (PIN flow) ────────────────────────────────────────────

  const handlePinLookup = () => {
    if (pinInput.length < 4) return;
    const pins = getPins();
    const profile = pins[pinInput];
    if (!profile) {
      setPinError('PIN not found. Please check your number or complete a new check-in.');
      return;
    }
    setPinError('');
    setReturnVisitor({ ...profile, pin: pinInput });
    setStep('returning');
  };

  const handleForgotPinLookup = () => {
    if (!forgotName.trim()) return;
    const pins = getPins();
    const searchName = forgotName.trim().toLowerCase();
    // Find PIN by matching name (case-insensitive)
    const match = Object.entries(pins).find(
      ([, profile]) => profile.name.toLowerCase() === searchName
    );
    if (match) {
      setFoundPin({ pin: match[0], name: match[1].name });
    } else {
      setFoundPin(null);
      setPinError('No PIN found for that name. Please check in as a new visitor.');
    }
  };

  // ── Check-out ───────────────────────────────────────────────────────────────

  const handleCheckOut = async (record) => {
    if (submitting) return;
    setSubmitting(true);
    try { await onCheckOut(record.personId); } catch { /* optimistic */ }
    showSuccess(`Goodbye, ${record.personName}! You have been checked out.`);
    setSubmitting(false);
  };

  // ── Returning visitor check-in with badge ───────────────────────────────────

  const handleReturnCheckInWithBadge = async () => {
    if (!returnHost.trim() || submitting) return;
    setSubmitting(true);
    const visitorId = `visitor-${Date.now()}`;
    const badge = {
      name: returnVisitor.name,
      type: returnVisitor.type,
      host: returnHost.trim(),
      organization: returnVisitor.organization || '',
      photo: returnPhoto || '',
      date: new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
    try {
      await onCheckIn({
        personId:     visitorId,
        personId_sp:  0,
        personName:   returnVisitor.name,
        personType:   returnVisitor.type,
        purpose:      '',
        host:         returnHost.trim(),
        organization: returnVisitor.organization || '',
      });
    } catch { /* optimistic */ }
    setBadgeData(badge);
    setSuccessMsg(`Welcome back, ${returnVisitor.name}!`);
    setStep('return-success');
    setSubmitting(false);
  };

  // ─── Shared back button ────────────────────────────────────────────────────

  const BackButton = ({ label = 'Back', onClick = goHome }) => (
    <button
      onClick={onClick}
      className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors text-sm mb-4"
    >
      <ArrowLeft className="w-4 h-4" /> {label}
    </button>
  );

  // ═══ HOME ════════════════════════════════════════════════════════════════════

  if (step === 'home') return (
    <div className="max-w-lg mx-auto flex flex-col items-center py-10 space-y-8">
      <div className="text-center">
        <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg">
          <Building2 className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-800">Welcome to</h1>
        <h2 className="text-2xl font-bold text-blue-600 mt-1">Evoke Behavioral Health</h2>
        <p className="text-gray-500 mt-3 text-sm">
          Please sign in when you arrive and sign out when you leave the building.
        </p>
      </div>

      {/* Check In / Check Out */}
      <div className="grid grid-cols-2 gap-5 w-full">
        <button
          onClick={() => setStep('checkin')}
          className="flex flex-col items-center justify-center py-10 bg-blue-600 text-white rounded-2xl shadow-lg hover:bg-blue-700 active:scale-95 transition-all"
        >
          <LogIn className="w-12 h-12 mb-3" />
          <span className="text-xl font-bold">Check In</span>
          <span className="text-blue-200 text-xs mt-1">New visitor</span>
        </button>

        <button
          onClick={() => setStep('checkout')}
          className="flex flex-col items-center justify-center py-10 bg-orange-500 text-white rounded-2xl shadow-lg hover:bg-orange-600 active:scale-95 transition-all"
        >
          <LogOut className="w-12 h-12 mb-3" />
          <span className="text-xl font-bold">Check Out</span>
          <span className="text-orange-200 text-xs mt-1">Leaving</span>
        </button>
      </div>

      {/* Returning visitor PIN */}
      <button
        onClick={() => setStep('pin-entry')}
        className="w-full flex items-center justify-center gap-3 py-5 bg-gray-100 text-gray-700 rounded-2xl hover:bg-gray-200 active:scale-95 transition-all border-2 border-gray-200"
      >
        <KeyRound className="w-6 h-6 text-gray-500" />
        <div className="text-left">
          <div className="font-bold text-base">Returning Visitor</div>
          <div className="text-xs text-gray-500">Sign in with your PIN</div>
        </div>
      </button>

      <button onClick={onSwitchToBuilding} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
        ← Staff Admin View
      </button>
    </div>
  );

  // ═══ NEW VISITOR CHECK-IN ════════════════════════════════════════════════════

  if (step === 'checkin') return (
    <div className="max-w-lg mx-auto py-8">
      <BackButton />
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Check In</h2>
        <p className="text-gray-500 mt-1 text-sm">Please fill out your information below</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Enter your full name"
            className="w-full px-4 py-3.5 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            I am a <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {VISITOR_TYPES.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                className={`py-3 rounded-xl font-medium text-sm transition-colors border-2 ${
                  form.type === opt.value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Choose a 4-digit PIN <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={4}
            value={form.pin}
            onChange={e => {
              setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }));
              setCheckInPinError('');
            }}
            placeholder="_ _ _ _"
            className={`w-full px-4 py-4 text-2xl text-center font-bold tracking-widest border-2 rounded-xl focus:outline-none transition-colors ${
              checkInPinError ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
            }`}
          />
          {checkInPinError && (
            <p className="text-red-600 text-sm mt-1.5">{checkInPinError}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Who are you visiting / Client initials
          </label>
          <input
            type="text"
            value={form.host}
            onChange={e => setForm(f => ({ ...f, host: e.target.value }))}
            placeholder="e.g., J.S. or staff member name"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Purpose of Visit
          </label>
          <input
            type="text"
            value={form.purpose}
            onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
            placeholder="e.g., Therapy session, Pick-up, Meeting…"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        {['visitor', 'provider', 'other'].includes(form.type) && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Organization / Company
            </label>
            <input
              type="text"
              value={form.organization}
              onChange={e => setForm(f => ({ ...f, organization: e.target.value }))}
              placeholder="Your organization name"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Badge Photo <span className="text-gray-400 text-xs font-normal">(optional)</span>
          </label>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handlePhotoChange}
            className="hidden"
          />
          {form.photo ? (
            <div className="flex items-center gap-3">
              <img src={form.photo} alt="Preview" className="w-16 h-16 object-cover rounded-xl border-2 border-gray-200" />
              <button
                type="button"
                onClick={() => { setForm(f => ({ ...f, photo: '' })); photoInputRef.current.value = ''; }}
                className="text-sm text-red-500 hover:text-red-700 transition-colors"
              >
                Remove photo
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => photoInputRef.current.click()}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              <Camera className="w-5 h-5" /> Take / upload a photo
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-3 pt-6">
        <button onClick={goHome} className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
          Cancel
        </button>
        <button
          onClick={handleNewCheckIn}
          disabled={!form.name.trim() || !isValidPin(form.pin) || submitting}
          className="flex-1 py-3.5 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Saving…' : 'Check In'}
        </button>
      </div>
    </div>
  );

  // ═══ PIN ENTRY ════════════════════════════════════════════════════════════════

  if (step === 'pin-entry') return (
    <div className="max-w-sm mx-auto py-8">
      <BackButton />
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound className="w-8 h-8 text-gray-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Returning Visitor</h2>
        <p className="text-gray-500 mt-1 text-sm">Enter the 4-digit PIN from your last visit</p>
      </div>

      <div className="space-y-4">
        <input
          type="tel"
          inputMode="numeric"
          maxLength={4}
          value={pinInput}
          onChange={e => { setPinInput(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
          onKeyDown={e => { if (e.key === 'Enter') handlePinLookup(); }}
          placeholder="_ _ _ _"
          className={`w-full px-4 py-5 text-4xl text-center font-bold tracking-widest border-2 rounded-xl focus:outline-none transition-colors ${
            pinError ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
          }`}
          autoFocus
        />

        {pinError && (
          <p className="text-red-600 text-sm text-center">{pinError}</p>
        )}

        <button
          onClick={handlePinLookup}
          disabled={pinInput.length < 4}
          className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>

        <p className="text-center text-sm text-gray-400">
          <button onClick={() => { setPinError(''); setForgotName(''); setFoundPin(null); setStep('forgot-pin'); }} className="text-blue-500 underline">Forgot your PIN?</button>
        </p>

        <p className="text-center text-sm text-gray-400">
          First time visiting? Use <button onClick={() => { goHome(); setTimeout(() => setStep('checkin'), 0); }} className="text-blue-500 underline">Check In</button> instead.
        </p>
      </div>
    </div>
  );

  // ═══ FORGOT PIN — lookup by name ═══════════════════════════════════════════

  if (step === 'forgot-pin') return (
    <div className="max-w-sm mx-auto py-8">
      <BackButton onClick={() => setStep('pin-entry')} label="Back" />
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound className="w-8 h-8 text-gray-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Forgot Your PIN?</h2>
        <p className="text-gray-500 mt-1 text-sm">Enter your full name to look up your PIN</p>
      </div>

      <div className="space-y-4">
        <input
          type="text"
          value={forgotName}
          onChange={e => { setForgotName(e.target.value); setPinError(''); setFoundPin(null); }}
          onKeyDown={e => { if (e.key === 'Enter') handleForgotPinLookup(); }}
          placeholder="Enter your full name"
          className={`w-full px-4 py-4 text-lg border-2 rounded-xl focus:outline-none transition-colors ${
            pinError && !foundPin ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-blue-500'
          }`}
          autoFocus
        />

        {pinError && !foundPin && (
          <p className="text-red-600 text-sm text-center">{pinError}</p>
        )}

        {foundPin && (
          <div className="w-full bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center">
            <p className="text-sm font-semibold text-green-700 mb-2">PIN found for {foundPin.name}</p>
            <div className="text-5xl font-bold text-green-700 tracking-widest">{foundPin.pin}</div>
            <p className="text-xs text-green-500 mt-3">Use this PIN to check in.</p>
          </div>
        )}

        {!foundPin && (
          <button
            onClick={handleForgotPinLookup}
            disabled={!forgotName.trim()}
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Look Up PIN
          </button>
        )}

        {foundPin && (
          <button
            onClick={() => { setForgotName(''); setFoundPin(null); setPinInput(''); setStep('pin-entry'); }}
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors"
          >
            Return to PIN Entry
          </button>
        )}

        <p className="text-center text-sm text-gray-400">
          Can't find your name? <button onClick={() => { goHome(); setTimeout(() => setStep('checkin'), 0); }} className="text-blue-500 underline">Check in as new visitor</button>
        </p>
      </div>
    </div>
  );

  // ═══ RETURNING VISITOR — enter who they're visiting ══════════════════════════

  if (step === 'returning' && returnVisitor) return (
    <div className="max-w-sm mx-auto py-8">
      <BackButton onClick={() => setStep('pin-entry')} label="Back" />
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Welcome back,</h2>
        <p className="text-2xl font-bold text-blue-600">{returnVisitor.name}!</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Who are you visiting / Client initials <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={returnHost}
            onChange={e => setReturnHost(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && returnHost.trim()) handleReturnCheckInWithBadge(); }}
            placeholder="e.g., J.S. or staff member name"
            className="w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Badge Photo <span className="text-gray-400 text-xs font-normal">(optional)</span>
          </label>
          <input
            ref={returnPhotoInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleReturnPhotoChange}
            className="hidden"
          />
          {returnPhoto ? (
            <div className="flex items-center gap-3">
              <img src={returnPhoto} alt="Preview" className="w-16 h-16 object-cover rounded-xl border-2 border-gray-200" />
              <button
                type="button"
                onClick={() => { setReturnPhoto(''); returnPhotoInputRef.current.value = ''; }}
                className="text-sm text-red-500 hover:text-red-700 transition-colors"
              >
                Remove photo
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => returnPhotoInputRef.current.click()}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
            >
              <Camera className="w-5 h-5" /> Take / upload a photo
            </button>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={goHome} className="flex-1 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleReturnCheckInWithBadge}
            disabled={!returnHost.trim() || submitting}
            className="flex-1 py-3.5 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Saving…' : 'Check In'}
          </button>
        </div>
      </div>
    </div>
  );

  // ═══ CHECK-OUT ════════════════════════════════════════════════════════════════

  if (step === 'checkout') return (
    <div className="max-w-lg mx-auto py-8">
      <BackButton />
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Check Out</h2>
        <p className="text-gray-500 mt-1 text-sm">Select your name to check out</p>
      </div>

      {currentlyIn.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No one is currently checked in.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {currentlyIn.map(record => (
            <button
              key={record.personId}
              onClick={() => handleCheckOut(record)}
              disabled={submitting}
              className="w-full flex items-center justify-between px-5 py-4 bg-white border-2 border-gray-200 rounded-xl hover:border-orange-400 hover:bg-orange-50 transition-colors group text-left disabled:opacity-50"
            >
              <div>
                <div className="font-semibold text-gray-800 text-base">{record.personName}</div>
                <div className="text-sm text-gray-400 mt-0.5 capitalize">
                  {record.personType} · Checked in at {fmt(record.checkInTime)}
                </div>
              </div>
              <div className="flex items-center gap-2 text-orange-500 group-hover:text-orange-600 font-semibold whitespace-nowrap ml-4">
                <LogOut className="w-5 h-5" /> Check Out
              </div>
            </button>
          ))}
        </div>
      )}

      <button onClick={goHome} className="w-full mt-4 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
        Back
      </button>
    </div>
  );

  // ═══ PIN SHOWN AFTER FIRST CHECK-IN ══════════════════════════════════════════

  if (step === 'pin-success') return (
    <>
      <div className="max-w-sm mx-auto flex flex-col items-center py-12 space-y-5 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">{successMsg}</h2>
        </div>

        <div className="w-full bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
          <p className="text-sm font-semibold text-blue-700 mb-2">Your PIN</p>
          <div className="text-6xl font-bold text-blue-700 tracking-widest">{newPin}</div>
          <p className="text-xs text-blue-500 mt-3">
            Use this PIN on your next visit to skip the full form.
          </p>
        </div>

        {badgeData && (
          <button
            onClick={handlePrintBadge}
            className="w-full flex items-center justify-center gap-3 py-4 bg-gray-800 text-white rounded-xl font-bold text-lg hover:bg-gray-900 transition-colors"
          >
            <Printer className="w-6 h-6" />
            Print Visitor Badge
          </button>
        )}

        <button
          onClick={goHome}
          className="text-gray-400 text-sm hover:text-gray-600 transition-colors"
        >
          Skip — return to home screen
        </button>
      </div>

      {/* Printable Badge (hidden on screen, visible when printing) */}
      {badgeData && (
        <div className="print-badge">
          {badgeData.photo && (
            <div className="badge-photo">
              <img src={badgeData.photo} alt="Visitor" />
            </div>
          )}
          <div className="badge-header">VISITOR</div>
          <div className="badge-name">{badgeData.name}</div>
          <div className="badge-type">
            {VISITOR_TYPES.find(t => t.value === badgeData.type)?.label || badgeData.type}
          </div>
          <div className="badge-details">
            {badgeData.host && <div>Visiting: {badgeData.host}</div>}
            {badgeData.organization && <div>From: {badgeData.organization}</div>}
          </div>
          <div className="badge-date">
            {badgeData.date} · {badgeData.time}
          </div>
        </div>
      )}
    </>
  );

  // ═══ RETURN VISITOR SUCCESS (with badge option) ═════════════════════════════

  if (step === 'return-success') return (
    <>
      <div className="max-w-sm mx-auto flex flex-col items-center py-12 space-y-5 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">{successMsg}</h2>
        <p className="text-gray-500">You are checked in.</p>

        {badgeData && (
          <button
            onClick={handlePrintBadge}
            className="w-full flex items-center justify-center gap-3 py-4 bg-gray-800 text-white rounded-xl font-bold text-lg hover:bg-gray-900 transition-colors"
          >
            <Printer className="w-6 h-6" />
            Print Visitor Badge
          </button>
        )}

        <button
          onClick={goHome}
          className="text-gray-400 text-sm hover:text-gray-600 transition-colors"
        >
          Skip — return to home screen
        </button>
      </div>

      {/* Printable Badge */}
      {badgeData && (
        <div className="print-badge">
          {badgeData.photo && (
            <div className="badge-photo">
              <img src={badgeData.photo} alt="Visitor" />
            </div>
          )}
          <div className="badge-header">VISITOR</div>
          <div className="badge-name">{badgeData.name}</div>
          <div className="badge-type">
            {VISITOR_TYPES.find(t => t.value === badgeData.type)?.label || badgeData.type}
          </div>
          <div className="badge-details">
            {badgeData.host && <div>Visiting: {badgeData.host}</div>}
            {badgeData.organization && <div>From: {badgeData.organization}</div>}
          </div>
          <div className="badge-date">
            {badgeData.date} · {badgeData.time}
          </div>
        </div>
      )}
    </>
  );

  // ═══ GENERIC SUCCESS ══════════════════════════════════════════════════════════

  if (step === 'success') return (
    <div className="max-w-lg mx-auto flex flex-col items-center justify-center py-20 space-y-6 text-center">
      <div className="w-28 h-28 bg-green-100 rounded-full flex items-center justify-center">
        <Check className="w-14 h-14 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-800">{successMsg}</h2>
      <p className="text-gray-400 text-sm">Returning to home screen…</p>
    </div>
  );

  return null;
}
